import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { Loader2 } from 'lucide-react';

const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, loading, user } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!isAuthenticated) {
    // Save the intended destination (including search params) for redirect after login
    const intendedUrl = location.pathname + location.search;
    return <Navigate to="/auth" state={{ from: intendedUrl }} replace />;
  }

  // View Only customers can ONLY access /my-orders and /profile
  if (user?.account_type === 'view_only') {
    const allowedPaths = ['/my-orders', '/profile', '/auth'];
    if (!allowedPaths.some(path => location.pathname.startsWith(path))) {
      return <Navigate to="/my-orders" replace />;
    }
  }

  // Warehouse managers should only access /warehouse route and task-manager for incoming
  if (user?.role === 'warehouse_manager') {
    const allowedPaths = ['/warehouse', '/auth', '/task-manager/incoming'];
    if (!allowedPaths.some(path => location.pathname.startsWith(path))) {
      return <Navigate to="/warehouse" replace />;
    }
  }

  // Warehouse heads have FULL task-manager access like staff users
  if (user?.role === 'warehouse_head') {
    const allowedPaths = ['/warehouse-head', '/auth', '/task-manager'];
    if (!allowedPaths.some(path => location.pathname.startsWith(path))) {
      return <Navigate to="/warehouse-head" replace />;
    }
  }

  // Salesmen ONLY see their own panel + profile + auth. Everything else
  // (task-manager, price-master, analytics, cheques, admin pages) is blocked.
  if (user?.role === 'salesman') {
    const allowedPaths = ['/salesman', '/auth', '/profile'];
    if (!allowedPaths.some(path => location.pathname.startsWith(path))) {
      return <Navigate to="/salesman" replace />;
    }
  }

  return children;
};

export default ProtectedRoute;
